import { PRISMA_EXECUTION_STATUS } from "@pericles/shared";

import { normalizeMeetingStatusForApi } from "../domain/statusAdapters";
import type {
  MeetingAudioBehaviorAnalysis,
  CreateProjectMeetingInput,
  MeetingAudioSession,
  MeetingAudioTranscriptionResult,
  MeetingGeneratedArtifacts,
  GeneratedMeetingQuestion,
  ProjectMeeting,
  ProjectMeetingQuestion,
  ProjectMeetingStatus,
  UpdateProjectMeetingInput,
} from "../types/meetings";

interface MeetingsResponse {
  ok?: boolean;
  meetings?: ProjectMeeting[];
  meeting?: ProjectMeeting;
  error?: string;
}

interface GenerateQuestionsResponse {
  ok?: boolean;
  questions?: GeneratedMeetingQuestion[];
  error?: string;
}

interface GenerateArtifactsResponse {
  ok?: boolean;
  requirements?: MeetingGeneratedArtifacts["requirements"];
  specSections?: MeetingGeneratedArtifacts["specSections"];
  questionsAndAnswers?: MeetingGeneratedArtifacts["questionsAndAnswers"];
  summaries?: MeetingGeneratedArtifacts["summaries"];
  dslSuggestions?: MeetingGeneratedArtifacts["dslSuggestions"];
  error?: string;
}

interface ListArtifactsResponse {
  ok?: boolean;
  requirements?: MeetingGeneratedArtifacts["requirements"];
  specSections?: MeetingGeneratedArtifacts["specSections"];
  questionsAndAnswers?: MeetingGeneratedArtifacts["questionsAndAnswers"];
  summaries?: MeetingGeneratedArtifacts["summaries"];
  dslSuggestions?: MeetingGeneratedArtifacts["dslSuggestions"];
  error?: string;
}

interface MeetingAudioSessionResponse {
  ok?: boolean;
  session?: MeetingAudioSession;
  error?: string;
}

interface FinalizeMeetingAudioSessionResponse extends MeetingAudioSessionResponse {
  transcript?: MeetingAudioTranscriptionResult;
  analysis?: MeetingAudioBehaviorAnalysis;
}

interface MeetingAudioSessionCreateInput {
  provider?: string;
  language?: string;
}

interface FinalizeMeetingAudioSessionInput {
  applyToMeeting?: boolean;
  transcription?: {
    provider?: string;
    model?: string;
    language?: string;
    responseFormat?: string;
    diarize?: boolean;
  };
}

interface FinalizeMeetingAudioSessionResult {
  session: MeetingAudioSession;
  transcript: MeetingAudioTranscriptionResult;
  analysis: MeetingAudioBehaviorAnalysis;
}

interface LatestMeetingAudioAnalysisResponse extends MeetingAudioSessionResponse {
  found?: boolean;
  transcript?: MeetingAudioTranscriptionResult;
  analysis?: MeetingAudioBehaviorAnalysis;
}

export interface LatestMeetingAudioAnalysisResult {
  session: MeetingAudioSession;
  transcript: MeetingAudioTranscriptionResult;
  analysis: MeetingAudioBehaviorAnalysis;
}

function ensureProjectId(projectId: string | undefined): asserts projectId is string {
  if (!projectId) {
    throw new Error("project id is required");
  }
}

function inferAudioMimeTypeFromFilename(filename: string | undefined): string | null {
  const normalized = String(filename ?? "").trim().toLowerCase();
  if (!normalized) return null;
  const name = normalized.toLowerCase();
  const extension = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  if (extension === ".webm") return "audio/webm";
  if (extension === ".wav") return "audio/wav";
  if (extension === ".mp3" || extension === ".mpeg") return "audio/mpeg";
  if (extension === ".m4a" || extension === ".mp4") return "audio/mp4";
  if (extension === ".ogg") return "audio/ogg";
  if (extension === ".flac") return "audio/flac";
  if (extension === ".aac") return "audio/aac";
  return null;
}

function getApiErrorMessage(
  response: Response,
  payload: { error?: string } | undefined,
  fallbackPrefix: string,
): string {
  return payload?.error ?? `${fallbackPrefix} (status ${response.status})`;
}

export async function fetchProjectMeetings(projectId: string): Promise<ProjectMeeting[]> {
  ensureProjectId(projectId);

  const response = await fetch(`/api/projects/${projectId}/meetings`);
  const payload = (await response.json()) as MeetingsResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to load meetings (status ${response.status})`;
    throw new Error(message);
  }

  return payload.meetings ?? [];
}

export async function fetchProjectMeeting(projectId: string, meetingId: string): Promise<ProjectMeeting> {
  ensureProjectId(projectId);

  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const response = await fetch(`/api/projects/${projectId}/meetings/${meetingId}`);
  const payload = (await response.json()) as MeetingsResponse;

  if (!response.ok || payload.ok === false || !payload.meeting) {
    const message = payload?.error ?? `Failed to fetch meeting (status ${response.status})`;
    throw new Error(message);
  }

  return payload.meeting;
}

export async function createProjectMeeting(
  projectId: string,
  input: CreateProjectMeetingInput,
): Promise<ProjectMeeting> {
  ensureProjectId(projectId);

  const body: CreateProjectMeetingInput = {
    ...input,
    title: input.title.trim(),
    templateKey: input.templateKey.trim(),
    status: normalizeMeetingStatusForApi(input.status),
  };

  const response = await fetch(`/api/projects/${projectId}/meetings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const payload = (await response.json()) as MeetingsResponse;

  if (!response.ok || payload.ok === false || !payload.meeting) {
    const message = payload?.error ?? `Failed to create meeting (status ${response.status})`;
    throw new Error(message);
  }

  return payload.meeting;
}

export async function updateProjectMeeting(
  projectId: string,
  meetingId: string,
  input: UpdateProjectMeetingInput,
): Promise<ProjectMeeting> {
  ensureProjectId(projectId);

  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const body =
    input.status === undefined
      ? input
      : ({
          ...input,
          status: normalizeMeetingStatusForApi(input.status),
        } satisfies UpdateProjectMeetingInput);

  const response = await fetch(`/api/projects/${projectId}/meetings/${meetingId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const payload = (await response.json()) as MeetingsResponse;

  if (!response.ok || payload.ok === false || !payload.meeting) {
    const message = payload?.error ?? `Failed to update meeting (status ${response.status})`;
    throw new Error(message);
  }

  return payload.meeting;
}

export async function generateMeetingQuestions(
  projectId: string,
  params: { meetingTitle?: string; templateKey?: string },
): Promise<GeneratedMeetingQuestion[]> {
  ensureProjectId(projectId);

  const response = await fetch(`/api/projects/${projectId}/meetings/generate-questions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      meetingTitle: params.meetingTitle,
      templateKey: params.templateKey,
    }),
  });

  const payload = (await response.json()) as GenerateQuestionsResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to generate meeting questions (status ${response.status})`;
    throw new Error(message);
  }

  return payload.questions ?? [];
}

export async function generateMeetingArtifacts(
  projectId: string,
  meetingId: string,
): Promise<MeetingGeneratedArtifacts> {
  ensureProjectId(projectId);

  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/generate-artifacts`,
    { method: "POST" },
  );

  const payload = (await response.json()) as GenerateArtifactsResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to generate meeting artifacts (status ${response.status})`;
    throw new Error(message);
  }

  return {
    questionsAndAnswers: payload.questionsAndAnswers ?? [],
    summaries: payload.summaries ?? [],
    dslSuggestions: payload.dslSuggestions ?? [],
    requirements: payload.requirements ?? [],
    specSections: payload.specSections ?? [],
  } satisfies MeetingGeneratedArtifacts;
}

export async function fetchMeetingArtifacts(
  projectId: string,
  meetingId: string,
  params: { sourceType?: string | null } = {},
): Promise<MeetingGeneratedArtifacts> {
  ensureProjectId(projectId);

  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const sourceType = params.sourceType?.trim();
  const query = new URLSearchParams();
  if (sourceType) {
    query.set("sourceType", sourceType);
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/artifacts${
      query.toString() ? `?${query.toString()}` : ""
    }`,
  );

  const payload = (await response.json()) as ListArtifactsResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to fetch meeting artifacts (status ${response.status})`;
    throw new Error(message);
  }

  return {
    questionsAndAnswers: payload.questionsAndAnswers ?? [],
    summaries: payload.summaries ?? [],
    dslSuggestions: payload.dslSuggestions ?? [],
    requirements: payload.requirements ?? [],
    specSections: payload.specSections ?? [],
  } satisfies MeetingGeneratedArtifacts;
}

export async function updateProjectMeetingQuestions(
  projectId: string,
  meetingId: string,
  questions: ProjectMeetingQuestion[],
): Promise<ProjectMeeting> {
  ensureProjectId(projectId);

  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/questions`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ questions }),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to update meeting questions: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as ProjectMeeting;
}

export async function deleteProjectMeeting(projectId: string, meetingId: string): Promise<void> {
  ensureProjectId(projectId);

  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to delete meeting: ${response.status} ${response.statusText}`);
  }
}

export async function createMeetingAudioSession(
  projectId: string,
  meetingId: string,
  input: MeetingAudioSessionCreateInput = {},
): Promise<MeetingAudioSession> {
  ensureProjectId(projectId);
  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/audio/sessions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        provider: input.provider,
        language: input.language,
      }),
    },
  );
  const payload = (await response.json()) as MeetingAudioSessionResponse;

  if (!response.ok || payload.ok === false || !payload.session) {
    throw new Error(getApiErrorMessage(response, payload, "Failed to create meeting audio session"));
  }

  return payload.session;
}

export async function getMeetingAudioSession(
  projectId: string,
  meetingId: string,
  sessionId: string,
): Promise<MeetingAudioSession> {
  ensureProjectId(projectId);
  if (!meetingId) {
    throw new Error("meeting id is required");
  }
  if (!sessionId) {
    throw new Error("session id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/audio/sessions/${encodeURIComponent(sessionId)}`,
  );
  const payload = (await response.json()) as MeetingAudioSessionResponse;

  if (!response.ok || payload.ok === false || !payload.session) {
    throw new Error(getApiErrorMessage(response, payload, "Failed to load meeting audio session"));
  }

  return payload.session;
}

export async function uploadMeetingAudioChunk(
  projectId: string,
  meetingId: string,
  sessionId: string,
  chunk: Blob,
  options: { sequence?: number; filename?: string } = {},
): Promise<MeetingAudioSession> {
  ensureProjectId(projectId);
  if (!meetingId) {
    throw new Error("meeting id is required");
  }
  if (!sessionId) {
    throw new Error("session id is required");
  }
  if (!(chunk instanceof Blob) || chunk.size === 0) {
    throw new Error("audio chunk is required");
  }

  const formData = new FormData();
  const filename = options.filename?.trim() || "meeting-audio.webm";
  const preferredType = chunk.type && chunk.type !== "application/octet-stream"
    ? chunk.type
    : inferAudioMimeTypeFromFilename(filename);
  const payloadChunk = preferredType ? new Blob([chunk], { type: preferredType }) : chunk;
  formData.append("file", payloadChunk, filename);
  if (typeof options.sequence === "number" && Number.isFinite(options.sequence)) {
    formData.append("sequence", String(Math.max(0, Math.floor(options.sequence))));
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/audio/sessions/${encodeURIComponent(sessionId)}/chunks`,
    {
      method: "POST",
      body: formData,
    },
  );
  const payload = (await response.json()) as MeetingAudioSessionResponse;

  if (!response.ok || payload.ok === false || !payload.session) {
    throw new Error(getApiErrorMessage(response, payload, "Failed to upload meeting audio chunk"));
  }

  return payload.session;
}

export async function finalizeMeetingAudioSession(
  projectId: string,
  meetingId: string,
  sessionId: string,
  input: FinalizeMeetingAudioSessionInput = {},
): Promise<FinalizeMeetingAudioSessionResult> {
  ensureProjectId(projectId);
  if (!meetingId) {
    throw new Error("meeting id is required");
  }
  if (!sessionId) {
    throw new Error("session id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/audio/sessions/${encodeURIComponent(sessionId)}/finalize`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        applyToMeeting: input.applyToMeeting,
        transcription: input.transcription,
      }),
    },
  );
  const payload = (await response.json()) as FinalizeMeetingAudioSessionResponse;

  if (!response.ok || payload.ok === false || !payload.session || !payload.transcript || !payload.analysis) {
    throw new Error(getApiErrorMessage(response, payload, "Failed to finalize meeting audio session"));
  }

  return {
    session: payload.session,
    transcript: payload.transcript,
    analysis: payload.analysis,
  };
}

export async function analyzeMeetingAudioSession(
  projectId: string,
  meetingId: string,
  sessionId: string,
  input: FinalizeMeetingAudioSessionInput = {},
): Promise<FinalizeMeetingAudioSessionResult> {
  ensureProjectId(projectId);
  if (!meetingId) {
    throw new Error("meeting id is required");
  }
  if (!sessionId) {
    throw new Error("session id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/audio/sessions/${encodeURIComponent(sessionId)}/analyze`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        transcription: input.transcription,
      }),
    },
  );
  const payload = (await response.json()) as FinalizeMeetingAudioSessionResponse;

  if (!response.ok || payload.ok === false || !payload.session || !payload.transcript || !payload.analysis) {
    throw new Error(getApiErrorMessage(response, payload, "Failed to analyze meeting audio session"));
  }

  return {
    session: payload.session,
    transcript: payload.transcript,
    analysis: payload.analysis,
  };
}

export async function fetchLatestMeetingAudioAnalysis(
  projectId: string,
  meetingId: string,
): Promise<LatestMeetingAudioAnalysisResult | null> {
  ensureProjectId(projectId);
  if (!meetingId) {
    throw new Error("meeting id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(meetingId)}/audio/analysis/latest`,
  );
  const payload = (await response.json()) as LatestMeetingAudioAnalysisResponse;

  if (!response.ok || payload.ok === false) {
    throw new Error(getApiErrorMessage(response, payload, "Failed to load latest meeting audio analysis"));
  }
  if (payload.found === false) {
    return null;
  }
  if (!payload.session || !payload.transcript || !payload.analysis) {
    return null;
  }

  return {
    session: payload.session,
    transcript: payload.transcript,
    analysis: payload.analysis,
  };
}
